/**
 * 조준 타겟 선택 (순수 — Phaser 비의존).
 *
 * 포신/조준선은 새 타겟을 잡을 때 "위험한 벽돌(화면상 가장 아래) → 같은 높이면 가까운 벽돌"을 쓴다.
 * 단, 한번 잡은 타겟은 그 블록이 후보에서 사라지거나 사용자가 수동으로 다른 블록을 지정하기 전까지 유지한다.
 * 보조로 진짜 동률·prev 소멸 시 id 사전순 결정론 tiebreak로 getChildren() 순서 변동에도 흔들리지 않는다.
 *
 * homingTrack.ts/bounceBounds.ts와 동일한 "순수 계산 분리" 패턴 — vitest로 떨림 회귀를 검증한다.
 */

/** 조준 후보 — 안정적 식별자(Brick gridX,gridY)와 좌표. NearestBrickLocator 반환형과 호환. */
export interface AimCandidate {
  /** 후보의 안정적 식별자. Brick은 `"${gridX},${gridY}"` 형식으로 제공한다. */
  readonly id: string;
  /** 후보의 월드 x 좌표(px). */
  readonly x: number;
  /** 후보의 월드 y 좌표(px). */
  readonly y: number;
}

/**
 * 발사대보다 아래쪽 후보는 자동/수동 조준 대상에서 제외한다.
 *
 * y축은 아래가 +이므로 `candidate.y < originY`만 조준 가능하다. 펼치기/접기 offset 동기나
 * 시간 압박으로 하단 후보가 잠깐 남아도 포신이 아래로 돌아가지 않게 하는 마지막 방어선이다.
 */
export function filterAimCandidatesAboveOrigin(
  candidates: readonly AimCandidate[],
  originY: number,
): AimCandidate[] {
  if (!Number.isFinite(originY)) return [];
  return candidates.filter((c) => Number.isFinite(c.x) && Number.isFinite(c.y) && c.y < originY);
}

/** y/거리 동률 판정 epsilon(px). 좌표 비교라 매우 작은 값이면 충분. */
const TIE_EPS = 1e-6;

/**
 * origin 기준 자동 조준 후보를 선택한다.
 *  - candidates가 비면 null.
 *  - forcedId(사용자 수동 조준)가 후보에 살아있으면 자동 우선순위 무관하게 그 후보를 즉시 반환.
 *    forcedId가 후보에 없으면(해제/파괴) 아래 자동 로직으로 떨어져 자동 우선순위로 복귀한다.
 *  - prevId가 후보에 살아있으면 그대로 유지한다. 자동 우선순위는 prev가 사라진 뒤에만 새 타겟을 고른다.
 *  - best = 화면상 가장 아래(y 최대) 후보. 같은 높이면 origin에서 가까운 후보. 동률이면 id 사전순 최소 → 결정론.
 *  - prevId가 후보에 없으면(최초/파괴 직후) best를 새 타겟으로 반환한다.
 * 비교는 px 거리로 수행한다.
 *
 * @param originX - 거리 측정 기준점 x (포신/발사 위치, px).
 * @param originY - 거리 측정 기준점 y (포신/발사 위치, px).
 * @param candidates - 이번 프레임에 살아있는 후보 목록.
 * @param prevId - 직전 프레임에 선택된 후보의 id. 최초 선택이거나 이전 타겟이 파괴된 경우 null.
 * @param stickyMarginPx - 기존 호출부 호환용. 음수 방어만 수행하며, 자동 타겟은 prev 생존 중 전환하지 않는다.
 * @param forcedId - 사용자가 클릭으로 수동 지정한 강제 조준 대상 id. 후보에 살아있으면 거리 무관 우선. 기본 null(자동).
 * @returns 선택된 후보, 또는 candidates가 비면 null.
 */
export function selectAimTarget(
  originX: number,
  originY: number,
  candidates: readonly AimCandidate[],
  prevId: string | null,
  stickyMarginPx: number,
  forcedId: string | null = null,
): AimCandidate | null {
  if (stickyMarginPx < 0) {
    throw new RangeError(`stickyMarginPx must be >= 0, got ${stickyMarginPx}`);
  }
  if (candidates.length === 0) return null;

  // 사용자 수동 조준(forcedId)이 아직 살아있으면 자동 우선순위/락온 상태를 무시하고 그 블럭을 계속 조준한다.
  // 후보에 없으면(파괴 또는 해제) 아래 자동 로직으로 복귀.
  if (forcedId !== null) {
    const forced = candidates.find((c) => c.id === forcedId);
    if (forced) return forced;
  }

  if (prevId !== null) {
    const prev = candidates.find((c) => c.id === prevId);
    if (prev) return prev;
  }

  const dist = (c: AimCandidate) => Math.hypot(c.x - originX, c.y - originY);
  const sameDangerY = (a: AimCandidate, b: AimCandidate): boolean => Math.abs(a.y - b.y) <= TIE_EPS;

  let best = candidates[0]!;
  let bestDist = dist(best);
  for (let i = 1; i < candidates.length; i++) {
    const c = candidates[i]!;
    const d = dist(c);
    const cIsLower = c.y > best.y + TIE_EPS;
    const sameY = sameDangerY(c, best);
    // 화면상 더 아래(위험)인 후보가 1순위. 같은 높이면 더 가까운 후보, 그래도 동률이면 id 사전순 최소.
    if (
      cIsLower ||
      (sameY && (d < bestDist - TIE_EPS || (Math.abs(d - bestDist) <= TIE_EPS && c.id < best.id)))
    ) {
      best = c;
      bestDist = d;
    }
  }

  return best;
}
